
import Request from './request';
import Util from './util';

import * as log from 'loglevel';

/**
 * Shopify Ajax Cart helper
 *
 * can be used as singleton via ShopifyCart.getInstance(..);
 *
 * docs: https://shopify.dev/docs/ajax-api/reference/cart
 *
 */
class ShopifyCart {
    constructor(options) {
        // Default settings
        let defaults = {
            api: '',
            requestOptions: {
                responseType: 'json',
                headers: {
                    'Content-type': 'application/json'
                }
            }
        };

        this.cache = null;

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});
    }

    static getInstance(options){
        if(ShopifyCart._instance){
            return ShopifyCart._instance;
        }

        // store instance on class as static property
        ShopifyCart._instance = new ShopifyCart(options); // options only required on first call

        return ShopifyCart._instance;
    }

    getCart(cached = false){

        if(cached && this.cache){
            return Promise.resolve(this.cache);
        }

        return this._cart().then( cart => {
            // store in cache
            this.cache = cart;

            return cart;
        });
    }

    getItemLine(cart, item){
        for (let i = 0; i < cart.items.length; i++) {
            const cartItem = cart.items[i];
            // compare key to get line
            if(item.key === cartItem.key){
                return i+1; // line 1 based index
            }
        }
        return -1;
    }

    toItem(id, quantity, properties){
        return { id, quantity, properties }; // key value same name syntax
    }

    addItem(item) {
        return this._cartAdd(item);
    }

    addItems(items, attributes) {
        // new ajax support for multiple items
        return this._cartAdd({ items, attributes }).then((response => response.items)); // unwrap items array
    }

    addItemFormData(item, onProgress){
        const formData = new FormData();

        formData.append('id', item.id);
        formData.append('quantity', item.quantity ? item.quantity : 1);
        formData.append('utf8', '✓');
        formData.append('form_type', 'product');

        if(item.properties){
            // loop over properties and attach to form (allows file attachments)
            for (let key in item.properties) {
                if (item.properties.hasOwnProperty(key)) {
                    formData.append('properties[' + key + ']', item.properties[key]);
                }
            }
        }

        return new Request('/cart/add.js', {
            method: 'POST',
            responseType: 'json',
            onProgress: onProgress,
            data: formData
        });
    }

    removeItem(line){
        return this.updateItem(line, this.toItem(null, 0));
    }

    removeItemById(id){
        return this.updateItemById(this.toItem(id, 0));
    }

    updateItem(line, item){
        return this._cartChange({
            line: line, // 1 based index
            quantity: item.quantity,
            properties: item.properties //   undefined, null, {}  does not update properties
        });
    }

    updateItemById(item){
        return this._cartChange(item);
    }

    // {12345678: 6, 12345680: 0}
    updateItemQuantitiesById(updates){
        return this._cartUpdate({ updates });
    }

    setNote(note){
        return this._cartUpdate({ note });
    }

    setAttributes(attributes){
        return this._cartUpdate({ attributes });
    }

    clear(){
        return this._cartClear();
    }

    _cart(){
        return this._cartRequest('/cart.js', {
            method: 'GET'
        });
    }

    _cartAdd(data){
        return this._cartRequest('/cart/add.js', {
            method: 'POST',
            data: data
        });
    }

    _cartUpdate(data){
        return this._cartRequest('/cart/update.js', {
            method: 'POST',
            data: data
        });
    }

    _cartChange(data){
        return this._cartRequest('/cart/change.js', {
            method: 'POST',
            data: data
        });
    }

    _cartClear(){
        return this._cartRequest('/cart/clear.js', {
            method: 'POST'
        });
    }

    _cartRequest(path, options){

        let requestOptions = Util.extend(this.settings.requestOptions, options);

        let requestUrl = this.settings.api + path;

        let request = new Request(requestUrl, requestOptions);

        return request
            .then((response) => {

                log.debug('[shopify-cart] ' + requestUrl + ' response', response.data);

                return response.data;
            })
            .catch((error) => {

                log.error('[shopify-cart] ' + requestUrl + ' error', error);

                throw error.data;
            });
    }
}

export default ShopifyCart;